import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { useAtsReportForMissionRole } from "hooks";
import { colors } from "style";
import { ROLE_ATS_REPORT_MODES } from "constants/index";
import Card from "components/Card";
import { H4, H5 } from "components/Text";
import { Grid, Row } from "components/Containers";
import { Spinner } from "components/Loader";
import { MessagesError } from "components/Messages";
import { css } from "@emotion/react";
import { Toggle } from "components/Form";
import { TOGGLE_SIZE } from "components/Form/Toggle";
import PageTitle from "components/PageTitle";

/**
 * RoleAtsReport
 *
 * @param {String} slug
 */
const RoleAtsReport = ({ slug }) => {
  const [mode, setMode] = useState(ROLE_ATS_REPORT_MODES.all_time);
  const { data, loading, error } = useAtsReportForMissionRole({ slug, mode });

  const handleToggle = () => {
    setMode((prev) =>
      prev === ROLE_ATS_REPORT_MODES.all_time ? ROLE_ATS_REPORT_MODES.last_week : ROLE_ATS_REPORT_MODES.all_time
    );
  };

  return (
    <>
      <Grid.col start={1} end={13}>
        <PageTitle
          subtitle={<FormattedMessage id="Missions.Roles.RoleAtsReport.Title" />}
          actionBar={
            <Row css={styles.toggle_row}>
              <span
                css={styles.toggle_label(mode === ROLE_ATS_REPORT_MODES.all_time)}
                onClick={() => setMode(ROLE_ATS_REPORT_MODES.all_time)}
              >
                <FormattedMessage id="Missions.Roles.RoleAtsReport.Mode.AllTime" />
              </span>
              <div css={styles.toggle_wrapper}>
                <Toggle
                  value={mode === ROLE_ATS_REPORT_MODES.last_week}
                  onChange={handleToggle}
                  disabled={loading}
                  size={TOGGLE_SIZE.small}
                />
              </div>
              <span
                css={styles.toggle_label(mode === ROLE_ATS_REPORT_MODES.last_week)}
                onClick={() => setMode(ROLE_ATS_REPORT_MODES.last_week)}
              >
                <FormattedMessage id="Missions.Roles.RoleAtsReport.Mode.LastWeek" />
              </span>
            </Row>
          }
        />
      </Grid.col>
      <Grid.col start={1} end={13}>
        <Card>
          {loading && (
            <Grid.col start={1} end={13}>
              <Spinner size="1.5rem" margin="1rem auto 0" />
            </Grid.col>
          )}

          {error && (
            <Grid.col start={1} end={13}>
              <MessagesError messageId="Missions.Roles.RoleAtsReport.Error" />
            </Grid.col>
          )}

          {!loading && !error && (
            <Grid cols={4}>
              <Grid.col>
                <MetricBox
                  title="Invitations"
                  data={[
                    { label: "Sent", value: data?.invitationsSent },
                    { label: "Accepted", value: data?.invitationsAccepted },
                  ]}
                />
              </Grid.col>

              <Grid.col>
                <MetricBox title="New Applications" data={[{ label: "Received", value: data?.applicationsReceived }]} />
              </Grid.col>

              <Grid.col>
                <MetricBox
                  title="Applications Processed"
                  data={[
                    { label: "Reviewed", value: data?.applicationsMovedToInReview },
                    { label: "Shortlisted", value: data?.applicationsMovedToShortlisted },
                  ]}
                />
              </Grid.col>

              <Grid.col>
                <MetricBox
                  title="Applications Finalized"
                  data={[
                    { label: "Selected", value: data?.applicationsMovedToSelected },
                    { label: "Not Selected", value: data?.applicationsMovedToNotSelected },
                    { label: "Withdrawn", value: data?.applicationsWithdrawn },
                  ]}
                />
              </Grid.col>
            </Grid>
          )}
        </Card>
      </Grid.col>
    </>
  );
};

/**
 * MetricBox
 *
 * @param {string} title
 * @param {Array} data
 */
const MetricBox = ({ title, data }) => (
  <div>
    <H4 css={styles.title}>{title}</H4>
    <div css={styles.metrics}>
      {data.map(({ label, value }, idx) => (
        <div key={idx} css={styles.metric_item}>
          <H5>{value ?? "—"}</H5>
          <span>{label}</span>
        </div>
      ))}
    </div>
  </div>
);

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    padding: 0.5rem 0;
  `,
  title: css`
    padding-bottom: 0.75rem;
  `,
  metrics: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem 1.5rem;
  `,
  metric_item: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  toggle_row: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 0.5rem;
    user-select: none;
  `,
  toggle_wrapper: css`
    transform: scale(1.4);
    display: flex;
    align-items: center;
  `,
  toggle_label: (isActive) => css`
    font-size: 1.4rem;
    font-weight: ${isActive ? 600 : 500};
    color: ${isActive ? colors.purpleRainBase : colors.grayAnatomyBase};
    cursor: pointer;
    transition: color 0.2s ease, font-weight 0.2s ease;

    &:hover {
      color: ${colors.purpleRainLight1};
    }
  `,
};

MetricBox.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number,
    })
  ).isRequired,
};

RoleAtsReport.propTypes = {
  slug: PropTypes.string.isRequired,
};

export default RoleAtsReport;
